import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Cookie } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const CONSENT_KEY = "manno_cookie_consent";
export const OPEN_COOKIE_PREFS_EVENT = "manno:open-cookie-preferences";

const readConsent = () => {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY));
  } catch {
    return null;
  }
};

const applyConsent = (consent) => {
  try {
    if (!window.posthog) return;
    if (consent.analytics) window.posthog.opt_in_capturing();
    else window.posthog.opt_out_capturing();
  } catch {
    /* strumento di analisi non disponibile */
  }
};

const PrefRow = ({ title, text, checked, disabled, onChange, testid }) => (
  <div className="flex items-start justify-between gap-4 bg-[#161D2B] border border-border rounded-xl p-4">
    <div>
      <p className="font-semibold text-sm">{title}</p>
      <p className="mt-1 text-xs text-gray-500 leading-relaxed">{text}</p>
    </div>
    <Switch
      data-testid={testid}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onChange}
      aria-label={title}
    />
  </div>
);

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const saved = readConsent();
    if (saved) {
      applyConsent(saved);
      return undefined;
    }
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const openPrefs = useCallback(() => {
    const saved = readConsent();
    setAnalytics(!!saved?.analytics);
    setMarketing(!!saved?.marketing);
    setVisible(false);
    setPrefsOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener(OPEN_COOKIE_PREFS_EVENT, openPrefs);
    return () => window.removeEventListener(OPEN_COOKIE_PREFS_EVENT, openPrefs);
  }, [openPrefs]);

  const save = (consent) => {
    const data = { necessary: true, ...consent, ts: new Date().toISOString() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
    applyConsent(data);
    setVisible(false);
    setPrefsOpen(false);
  };

  return (
    <>
      {visible && (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed z-[90] left-3 right-24 bottom-3 sm:left-auto sm:right-6 sm:bottom-24 sm:max-w-sm"
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Informativa sui cookie"
        >
          <div className="bg-[#111622] border border-border rounded-2xl shadow-2xl shadow-black/50 p-4">
            <div className="flex items-start gap-2.5">
              <Cookie className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-gray-400 leading-relaxed">
                Usiamo cookie tecnici e, solo con il tuo consenso, analisi anonime per migliorare il sito.{" "}
                <Link to="/cookie-policy" className="text-amber-500 hover:underline" data-testid="cookie-banner-policy-link">
                  Cookie Policy
                </Link>
              </p>
            </div>
            <div className="mt-3 flex gap-2 justify-end">
              <button
                data-testid="cookie-customize-btn"
                onClick={openPrefs}
                className="px-4 py-2 rounded-full border border-border text-gray-300 hover:border-amber-500/50 hover:text-amber-500 text-xs font-semibold transition-colors"
              >
                Personalizza
              </button>
              <button
                data-testid="cookie-accept-btn"
                onClick={() => save({ analytics: true, marketing: true })}
                className="px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-400 text-[#0A0D14] text-xs font-semibold transition-colors"
              >
                Accetta
              </button>
            </div>
          </div>
        </motion.div>
      )}

      <Dialog open={prefsOpen} onOpenChange={(open) => { setPrefsOpen(open); if (!open && !readConsent()) setVisible(true); }}>
        <DialogContent className="bg-[#111622] border-border text-gray-100 max-w-md" data-testid="cookie-preferences-modal">
          <DialogTitle className="font-display text-2xl font-extrabold uppercase tracking-tight">
            Preferenze cookie
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-400">
            Scegli quali categorie attivare. I cookie tecnici restano sempre attivi perché necessari al
            funzionamento del sito.
          </DialogDescription>
          <div className="grid gap-3 mt-2">
            <PrefRow
              title="Tecnici (necessari)"
              text="Ricordano le tue preferenze di consenso e mantengono attiva la sessione dell'area riservata. Sempre attivi."
              checked
              disabled
              testid="cookie-necessary-toggle"
            />
            <PrefRow
              title="Analitici (anonimi)"
              text="Ci aiutano a capire come viene usato il sito, in forma aggregata e anonima (PostHog)."
              checked={analytics}
              onChange={setAnalytics}
              testid="cookie-analytics-toggle"
            />
            <PrefRow
              title="Marketing"
              text="Cookie pubblicitari e di profilazione. Questo sito non ne installa: l'opzione è disponibile per completezza."
              checked={marketing}
              onChange={setMarketing}
              testid="cookie-marketing-toggle"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <button
              data-testid="cookie-save-preferences-btn"
              onClick={() => save({ analytics, marketing })}
              className="flex-1 px-5 py-2.5 rounded-full border border-border text-gray-300 hover:border-amber-500/50 hover:text-amber-500 text-sm font-semibold transition-colors"
            >
              Salva preferenze
            </button>
            <button
              data-testid="cookie-accept-all-btn"
              onClick={() => save({ analytics: true, marketing: true })}
              className="flex-1 px-5 py-2.5 rounded-full bg-amber-500 hover:bg-amber-400 text-[#0A0D14] text-sm font-semibold transition-colors"
            >
              Accetta tutto
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
